import type {
  AgentConfiguratorState,
  AgentCoordinatorState,
  AgentWorker,
} from '../types/agentConfigurator';
import { mergeVirtualUserPromptDefaultsIntoRecord } from './vuserDefaults';

export const NLT_ADMIN_TOOLS_SNAPSHOT_KEY = 'nlt_admin_tools';

export const NLT_ADMIN_EXTRA_TOOL_FIELDS = [
  'use_list_datasets_tool',
  'use_read_dataset_tool',
  'use_list_evaluators_tool',
  'use_run_evaluator_tool',
  'use_workflow_patch_tool',
] as const;

export type NltAdminExtraToolField = (typeof NLT_ADMIN_EXTRA_TOOL_FIELDS)[number];

export const NLT_ADMIN_AGENT_TOOL_FIELDS = [
  'use_config_patch_tool',
  ...NLT_ADMIN_EXTRA_TOOL_FIELDS,
] as const;

export type NltAdminAgentToolField = (typeof NLT_ADMIN_AGENT_TOOL_FIELDS)[number];

export const NLT_ADMIN_AGENT_TOOL_UI: Record<
  NltAdminAgentToolField,
  { label: string; description: string }
> = {
  use_config_patch_tool: {
    label: 'Config patch tool',
    description: 'Allows the agent to propose and apply config patches.',
  },
  use_list_datasets_tool: {
    label: 'List datasets',
    description: 'Lists datasets available for the integration.',
  },
  use_read_dataset_tool: {
    label: 'Read dataset',
    description: 'Reads the contents of a selected dataset.',
  },
  use_list_evaluators_tool: {
    label: 'List evaluators',
    description: 'Lists evaluators configured for the integration.',
  },
  use_run_evaluator_tool: {
    label: 'Run evaluator',
    description: 'Runs an evaluator against a dataset or scenario.',
  },
  use_workflow_patch_tool: {
    label: 'Workflow patch tool',
    description: 'Allows the agent to propose patches to the workflow graph. Off by default.',
  },
};

export function getNltAdminToolLabel(field: NltAdminAgentToolField): string {
  return NLT_ADMIN_AGENT_TOOL_UI[field].label;
}

export type NltAdminAgentToolState = Pick<
  AgentCoordinatorState,
  NltAdminAgentToolField
>;

export interface NltAdminWorkerToolsSnapshot {
  id?: string;
  name?: string;
  tools: NltAdminAgentToolState;
}

export interface NltAdminToolsSnapshot {
  coordinator: NltAdminAgentToolState;
  workers: NltAdminWorkerToolsSnapshot[];
}

export function resolveUseConfigPatchTool(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') return value;
  return fallback;
}

/**
 * Все дополнительные инструменты выключены, кроме патча конфига: он исторически
 * включён и его значение приходит параметром.
 *
 * Здесь и в двух функциях ниже поля берутся из NLT_ADMIN_AGENT_TOOL_FIELDS, а не
 * перечисляются заново: раньше добавление флага требовало правки в пяти местах,
 * и пропустить одно из них было легко.
 */
export function defaultNltAdminToolExtras(configPatchToolDefault = true): NltAdminAgentToolState {
  return NLT_ADMIN_AGENT_TOOL_FIELDS.reduce((acc, field) => {
    acc[field] = field === 'use_config_patch_tool' ? configPatchToolDefault : false;
    return acc;
  }, {} as NltAdminAgentToolState);
}

export function explicitNltAdminToolState(
  agent: Partial<NltAdminAgentToolState>
): NltAdminAgentToolState {
  return NLT_ADMIN_AGENT_TOOL_FIELDS.reduce((acc, field) => {
    acc[field] = isNltAdminToolEnabled(agent, field);
    return acc;
  }, {} as NltAdminAgentToolState);
}

export function normalizeNltAdminToolsFromInput(
  input: Record<string, unknown> | null | undefined,
  nltAdminMode: boolean,
  defaults: NltAdminAgentToolState = defaultNltAdminToolExtras(true)
): Partial<NltAdminAgentToolState> {
  if (!nltAdminMode) return {};
  return NLT_ADMIN_AGENT_TOOL_FIELDS.reduce((acc, field) => {
    const raw = input?.[field];
    acc[field] = typeof raw === 'boolean' ? raw : defaults[field];
    return acc;
  }, {} as NltAdminAgentToolState);
}

export function readNltAdminToolsSnapshot(raw: unknown): NltAdminToolsSnapshot | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Record<string, unknown>;
  const coordinatorRaw = source.coordinator;
  const workersRaw = source.workers;
  if (!coordinatorRaw || typeof coordinatorRaw !== 'object') return undefined;

  const coordinator = explicitNltAdminToolState(coordinatorRaw as Partial<NltAdminAgentToolState>);
  const workers = Array.isArray(workersRaw)
    ? workersRaw
        .filter((item) => item && typeof item === 'object')
        .map((item) => {
          const row = item as Record<string, unknown>;
          const toolsRaw = row.tools;
          return {
            id: typeof row.id === 'string' ? row.id : undefined,
            name: typeof row.name === 'string' ? row.name : undefined,
            tools: explicitNltAdminToolState(
              toolsRaw && typeof toolsRaw === 'object'
                ? (toolsRaw as Partial<NltAdminAgentToolState>)
                : {}
            ),
          };
        })
    : [];

  return { coordinator, workers };
}

function findWorkerToolsSnapshot(
  snapshots: NltAdminWorkerToolsSnapshot[],
  worker: AgentWorker
): NltAdminAgentToolState | undefined {
  const byId = snapshots.find((item) => item.id && item.id === worker.id);
  if (byId) return byId.tools;
  const workerName = String(worker.name || '').trim();
  if (!workerName) return undefined;
  const byName = snapshots.find((item) => String(item.name || '').trim() === workerName);
  return byName?.tools;
}

export function applyNltAdminToolsSnapshot(
  config: AgentConfiguratorState,
  snapshot: NltAdminToolsSnapshot | undefined
): AgentConfiguratorState {
  if (!snapshot) return config;

  return {
    ...config,
    coordinator: {
      ...config.coordinator,
      ...snapshot.coordinator,
    },
    workers: config.workers.map((worker) => {
      const tools = findWorkerToolsSnapshot(snapshot.workers, worker);
      return tools ? { ...worker, ...tools } : worker;
    }),
  };
}

export function extractNltAdminToolsSnapshot(cfg: AgentConfiguratorState): NltAdminToolsSnapshot {
  return {
    coordinator: explicitNltAdminToolState(cfg.coordinator),
    workers: cfg.workers.map((worker) => ({
      id: worker.id,
      name: worker.name,
      tools: explicitNltAdminToolState(worker),
    })),
  };
}

/** Persist explicit tool flags on agents and include a root snapshot for reload. */
export function serializeNltAdminConfigForSave(cfg: AgentConfiguratorState): Record<string, unknown> {
  const snapshot = extractNltAdminToolsSnapshot(cfg);
  return mergeVirtualUserPromptDefaultsIntoRecord({
    ...cfg,
    coordinator: {
      ...cfg.coordinator,
      ...snapshot.coordinator,
    },
    workers: cfg.workers.map((worker) => {
      const workerSnapshot = findWorkerToolsSnapshot(snapshot.workers, worker);
      return {
        ...worker,
        ...(workerSnapshot ?? explicitNltAdminToolState(worker)),
      };
    }),
    [NLT_ADMIN_TOOLS_SNAPSHOT_KEY]: snapshot,
  });
}

export function isNltAdminToolEnabled(
  agent: Partial<NltAdminAgentToolState>,
  field: NltAdminAgentToolField
): boolean {
  if (field === 'use_config_patch_tool') return agent.use_config_patch_tool !== false;
  return agent[field] === true;
}

export function toggleNltAdminTool(
  agent: Partial<NltAdminAgentToolState>,
  field: NltAdminAgentToolField
): Partial<NltAdminAgentToolState> {
  if (field === 'use_config_patch_tool') {
    return { use_config_patch_tool: agent.use_config_patch_tool === false };
  }
  return { [field]: !agent[field] };
}

export function applyNltAdminToolToggle<T extends AgentCoordinatorState | AgentWorker>(
  agent: T,
  field: NltAdminAgentToolField
): T {
  return { ...agent, ...toggleNltAdminTool(agent, field) };
}
